import React, { useRef, useState } from 'react';
import { Building2, Pencil, Upload, Loader, X, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import useAuthUser from '../../hooks/useAuthUser';
import { MODULE_UPLOAD } from '../../utils/moduleUpload.ts';
import { initUpload, uploadToGCS, completeUpload } from '../../utils/fileUpload';
import { calculateQuantitativeScoping } from '../../utils/entityScopingQuantitative.js';
import { updateMaterialityAssessmentReport } from './assessmentReportsEditApi.js';

export default function EntityScopingUploadEditCard({ assessment, user: userProp, onSaved }) {
  const user = useAuthUser(userProp);
  const fileInputRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const resolveUploadAuthToken = () => {
    let fromAuthUser = '';
    try {
      const stored = JSON.parse(localStorage.getItem('auth_user') || 'null');
      fromAuthUser = stored?.user_hash || stored?.userHash || '';
    } catch {
      fromAuthUser = '';
    }
    const userHash = String(
      localStorage.getItem('userHash') ||
        user?.user_hash ||
        user?.userHash ||
        fromAuthUser ||
        ''
    ).trim();
    if (userHash) {
      localStorage.setItem('userHash', userHash);
      return userHash;
    }
    const accessToken = String(localStorage.getItem('access_token') || '').trim();
    if (accessToken) {
      return accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    }
    return '';
  };

  const materialityThresholds = () => {
    const confirmed = assessment?.materiality_json?.confirmedMateriality || {};
    const prev = assessment?.entityscoping_json?.editableMateriality || {};
    return {
      overallMateriality:
        Number(prev.overallMateriality ?? confirmed.overallMateriality) || 0,
      planningMateriality:
        Number(prev.planningMateriality ?? confirmed.planningMateriality) || 0,
      deminimis: Number(prev.deminimis ?? confirmed.deminimis) || 0,
    };
  };

  const handleCancel = () => {
    setEditing(false);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const next = e.target.files?.[0] || null;
    if (!next) return;
    const lower = next.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
      toast.error('Use .csv, .xlsx, or .xls');
      return;
    }
    setFile(next);
  };

  const handleAnalyzeAndSave = async () => {
    if (!assessment?.id) return;
    if (!file) {
      toast.error('Please choose an entity trial balance file');
      return;
    }
    const authToken = resolveUploadAuthToken();

    setBusy(true);
    try {
      const initResult = await initUpload(file, authToken, MODULE_UPLOAD.ENTITY_SCOPING);
      await uploadToGCS(initResult.upload_url, file, initResult.content_type);
      const completeResult = await completeUpload(
        initResult.file_ref,
        file.name,
        file.type || initResult.content_type || 'application/octet-stream',
        file.size,
        authToken,
        MODULE_UPLOAD.ENTITY_SCOPING
      );
      const fileId = completeResult.uuid;
      if (!fileId) {
        throw new Error('Upload finished but no file id was returned.');
      }

      const analyzeHeaders = { 'Content-Type': 'application/json' };
      if (authToken) analyzeHeaders.Authorization = authToken;
      const response = await axiosInstance.post(
        '/api/entity-scoping/analyze-entities',
        { file_id: fileId, userHash: authToken },
        {
          headers: analyzeHeaders,
          timeout: 240000,
        }
      );
      const data = response?.data || {};
      const entities = Array.isArray(data?.entities) ? data.entities : [];
      if (entities.length === 0) {
        throw new Error(
          data?.message || data?.error || 'No entities found in the uploaded file.'
        );
      }

      const editableMateriality = materialityThresholds();
      const calculatedConclusions = calculateQuantitativeScoping(
        entities,
        editableMateriality.planningMateriality
      );
      const entityConclusions = { ...calculatedConclusions };
      const finalConclusions = { ...calculatedConclusions };

      const nextEntityScoping = {
        ...(assessment.entityscoping_json && typeof assessment.entityscoping_json === 'object'
          ? assessment.entityscoping_json
          : {}),
        entityScopingData: entities,
        entityConclusions,
        qualitativeComments: {},
        finalConclusions,
        editableMateriality,
        period: data.period || '',
        trialBalanceFile: { name: file.name, size: file.size },
      };

      const payload = {
        analysis_name: assessment.analysis_name,
        documents_json: assessment.documents_json || null,
        materiality_json: assessment.materiality_json,
        sox_cycle_json: assessment.sox_cycle_json,
        entityscoping_json: nextEntityScoping,
        ...(assessment.audit_year_id ? { audit_year_id: assessment.audit_year_id } : {}),
      };

      const saved = await updateMaterialityAssessmentReport(assessment.id, payload);
      if (!saved?.success && !saved?.id) {
        throw new Error(saved?.error || 'Update failed');
      }

      toast.success('Entity scoping saved');
      setEditing(false);
      setFile(null);
      onSaved?.({
        ...assessment,
        ...payload,
        id: assessment.id,
        entityscoping_json: nextEntityScoping,
      });
    } catch (err) {
      toast.error(
        resolve403ErrorMessage(
          err,
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to analyze entity file'
        )
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Entity Scoping</h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload an entity trial balance to add scoping for this assessment.
          </p>
        </div>
        {!editing && assessment?.id && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-white text-indigo-700 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-2 flex-shrink-0"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </button>
        )}
      </div>

      {!editing ? (
        <div className="text-center py-12">
          <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No entity scoping data available for this assessment</p>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle className="h-10 w-10 text-green-600" />
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="text-sm text-indigo-700 hover:underline disabled:opacity-50"
                >
                  Choose a different file
                </button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700 mb-1">Upload Entity Trial Balance</p>
                <p className="text-xs text-gray-500 mb-4">Supports .csv, .xlsx, .xls</p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Choose file
                </button>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={handleCancel}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !file}
              onClick={() => void handleAnalyzeAndSave()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Analyzing…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Analyze &amp; Save
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
